import { AnalyticsService } from 'base/analytics';
import { getProjectUrl } from 'base/blocks_routes';
import { delay, UnreachableError } from 'base/helpers';
import { FavIcon } from 'base/ui/fav_icon';
import { ToastQueue } from 'base/ui/toast';
import { useOnMount } from 'base/useOnMount';
import { BlocksService } from 'blocks/blocks_service';
import { Assistant } from 'blocks/editor/ai/assistant';
import { Project } from 'blocks/editor/model';
import { pageAndAllSubpages } from 'blocks/editor/model_helpers';
import { SitemapImportEntryDialog } from 'blocks/sitemap_import/sitemap_import_entry_dialog';
import { SitemapImportErrorDialog } from 'blocks/sitemap_import/sitemap_import_error_dialog';
import type { ImportCompletedResult } from 'blocks/sitemap_import/sitemap_import_importing_dialog';
import { SitemapImportImportingDialog } from 'blocks/sitemap_import/sitemap_import_importing_dialog';
import React, { useCallback } from 'react';

type Status =
  | {
      kind: 'entry';
    }
  | {
      kind: 'importing';
      domain: string;
    }
  | {
      kind: 'error';
      error: unknown;
    };

type ImportBehaviour =
  | {
      kind: 'import-into-new-project';
    }
  | {
      kind: 'import-into-existing-project';
      toastQueue: ToastQueue;
      assistant: Assistant;
      domain: string;
      project: Project;
    };

type SitemapImportDialogProps = {
  analytics: AnalyticsService;
  blocksService: BlocksService;
  onClose(): void;
  owner?: 'user' | 'team';
  importBehaviour: ImportBehaviour;
};

export function SitemapImportDialog({
  analytics,
  blocksService,
  onClose,
  owner = 'user',
  importBehaviour,
}: SitemapImportDialogProps) {
  const [status, setStatus] = React.useState<Status>(
    importBehaviour.kind === 'import-into-new-project'
      ? { kind: 'entry' }
      : {
          kind: 'importing',
          domain: importBehaviour.domain,
        },
  );

  useOnMount(() =>
    analytics.track({
      event: 'import_sitemap:import_sitemap_dialog_view',
      behaviour: importBehaviour.kind,
    }),
  );

  const handleClose = useCallback(() => {
    analytics.track({ event: 'import_sitemap:close_button_click' });
    onClose();
  }, [analytics, onClose]);

  const handleStartImport = React.useCallback(
    async ({ domain }: { domain: string }) => {
      analytics.track({
        event: 'import_sitemap:start_button_click',
        import_domain: domain,
      });
      setStatus({
        kind: 'importing',
        domain,
      });
    },
    [analytics],
  );

  const handleImportCompleted = useCallback(
    async (result: ImportCompletedResult) => {
      switch (result.kind) {
        case 'success': {
          switch (importBehaviour.kind) {
            case 'import-into-new-project': {
              analytics.track({
                event: 'import_sitemap:project_create',
                import_domain: result.domain,
              });
              window.location.href = getProjectUrl(result.projectToken, {
                sitemapImportedFrom: result.domain,
              });
              await delay(3_000); // Allow time for the browser to load the new page.
              onClose();
              break;
            }
            case 'import-into-existing-project': {
              const allPages = pageAndAllSubpages(
                importBehaviour.project.homePage,
              );
              importBehaviour.assistant.generatePages(allPages, {
                replaceExistingComponents: false,
                chooseComponents: false,
                generateCopy: false,
              });
              importBehaviour.toastQueue.push({
                label: `Sitemap imported from ${importBehaviour.domain}`,
                customRight: (
                  <FavIcon domain={importBehaviour.domain} size="small" />
                ),
                confetti: true,
              });
              onClose();
              break;
            }
          }
          break;
        }
        case 'error': {
          setStatus({
            kind: 'error',
            error: result.error,
          });
          break;
        }
        default: {
          throw new UnreachableError(result);
        }
      }
    },
    [analytics, onClose, importBehaviour],
  );

  switch (status.kind) {
    case 'entry': {
      return (
        <SitemapImportEntryDialog
          analytics={analytics}
          onClose={handleClose}
          onStartImport={handleStartImport}
        />
      );
    }
    case 'importing': {
      return (
        <SitemapImportImportingDialog
          blocksService={blocksService}
          domain={status.domain}
          onImportCompleted={handleImportCompleted}
          owner={owner}
          importBehaviour={
            importBehaviour.kind === 'import-into-new-project'
              ? { kind: 'import-into-new-project' }
              : {
                  kind: 'import-into-existing-project',
                  project: importBehaviour.project,
                }
          }
        />
      );
    }
    case 'error': {
      return (
        <SitemapImportErrorDialog error={status.error} onClose={handleClose} />
      );
    }
    default: {
      throw new UnreachableError(status);
    }
  }
}
